<template>
	<LField :label="label" v-slot="{ id }">
		<div class="multi-select" ref="rootEl">

			<button
				:id="id"
				type="button"
				class="trigger"
				:class="{ empty: !modelValue.length }"
				:aria-expanded="isOpen"
				@click="toggleOpen"
			>
				<span class="trigger-text">{{ triggerLabel }}</span>
				<LIcon name="chevron-down" :size="15" class="chev" />
			</button>

			<div v-if="isOpen" class="panel">
				<div class="panel-search">
					<LIcon name="magnifying-glass" :size="12" class="search-icon" />
					<input
						ref="searchEl"
						v-model="searchText"
						type="text"
						class="search-input"
						:placeholder="searchPlaceholder"
					/>
				</div>

				<div class="panel-actions">
					<button type="button" class="link-action" @click="selectAllVisible">Select all</button>
					<button type="button" class="link-action" @click="clearAll">Clear</button>
					<span class="panel-count">{{ selectionSummary }}</span>
				</div>

				<div class="panel-list">
					<LCheckbox
						v-for="option in visibleOptions"
						:key="option.value"
						class="option-row"
						:label="option.label"
						:model-value="isSelected(option.value)"
						@update:model-value="toggleOption(option.value)"
					/>
					<div v-if="!visibleOptions.length" class="option-empty">No matches</div>
				</div>
			</div>

		</div>
	</LField>
</template>




<script setup>
// §8.5 MultiSelect — a checkbox-driven picker for choosing several items from a
// list. Built from LField + LCheckbox so it carries the same field chrome and
// control styling as the rest of the data-entry set.
// Options: [{ value, label }]. Model: array of values ([] meaning "none" / "all",
// depending on how the caller reads an empty selection).
import { ref, computed, onBeforeUnmount, nextTick } from 'vue';
import LField from './Field.vue';
import LCheckbox from './Checkbox.vue';
import LIcon from '../base/Icon.vue';

//-- Props
const props = defineProps({
	label: { type: String, default: null },
	options: { type: Array, default: () => [] },

	// Shown when nothing is selected — callers where an empty selection means
	// "everything" can pass "All clients" etc.; the default reads as a plain
	// placeholder for callers where it means "nothing chosen yet".
	emptyLabel: { type: String, default: 'Select…' },

	searchPlaceholder: { type: String, default: 'Search…' },
});

const modelValue = defineModel({ default: () => [] });





//-- Data/Reactive State
const rootEl = ref(null);
const searchEl = ref(null);
const isOpen = ref(false);
const searchText = ref('');





//-- Computed Properties
const visibleOptions = computed(() =>
{
	const query = searchText.value.trim().toLowerCase();
	if (!query) return props.options;

	return props.options.filter((option) => String(option.label).toLowerCase().includes(query));
});


// What the closed control reads: the single selection by name, otherwise a count.
const triggerLabel = computed(() =>
{
	const selected = modelValue.value;
	if (!selected.length) return props.emptyLabel;

	if (selected.length === 1)
	{
		const option = props.options.find((o) => o.value === selected[0]);
		return option ? option.label : String(selected[0]);
	}

	return selected.length + ' selected';
});


const selectionSummary = computed(() =>
{
	const count = modelValue.value.length;
	if (!count) return props.emptyLabel;

	return count + ' of ' + props.options.length;
});





//-- Methods
const isSelected = (value) => modelValue.value.includes(value);


const toggleOption = (value) =>
{
	modelValue.value = isSelected(value)
		? modelValue.value.filter((v) => v !== value)
		: [...modelValue.value, value];
};


const selectAllVisible = () =>
{
	const visibleValues = visibleOptions.value.map((o) => o.value);
	modelValue.value = [...new Set([...modelValue.value, ...visibleValues])];
};


const clearAll = () =>
{
	modelValue.value = [];
};


const toggleOpen = async () =>
{
	isOpen.value = !isOpen.value;

	if (!isOpen.value)
	{
		document.removeEventListener('mousedown', onDocumentMouseDown);
		return;
	}

	searchText.value = '';
	document.addEventListener('mousedown', onDocumentMouseDown);

	await nextTick();
	if (searchEl.value) searchEl.value.focus();
};


const onDocumentMouseDown = (event) =>
{
	if (rootEl.value && rootEl.value.contains(event.target)) return;

	isOpen.value = false;
	document.removeEventListener('mousedown', onDocumentMouseDown);
};





//-- Lifecycle Hooks
onBeforeUnmount(() =>
{
	document.removeEventListener('mousedown', onDocumentMouseDown);
});
</script>




<style scoped>
.multi-select
{
	position: relative;
}

.trigger
{
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--sp-2);
	width: 100%;
	font-family: var(--font-ui);
	font-size: var(--text-base);
	color: var(--text);
	text-align: left;
	padding: 9px 12px;
	border: 1px solid var(--line);
	border-radius: var(--r);
	background: var(--surface);
	min-height: var(--target-min);
	cursor: pointer;
}

.trigger.empty
{
	color: var(--text-3);
}

.trigger:hover
{
	border-color: var(--line-strong);
}

.trigger:focus-visible
{
	border-color: var(--interactive);
}

.trigger-text
{
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.chev
{
	color: var(--icon);
	flex: none;
}

.panel
{
	position: absolute;
	z-index: 20;
	top: calc(100% + 4px);
	left: 0;
	min-width: 100%;
	max-width: 340px;
	background: var(--surface);
	border: 1px solid var(--line);
	border-radius: var(--r);
	box-shadow: var(--shadow);
	overflow: hidden;
}

.panel-search
{
	position: relative;
	display: flex;
	align-items: center;
	padding: var(--sp-2);
	border-bottom: 1px solid var(--line-soft);
}

.search-icon
{
	position: absolute;
	left: 18px;
	color: var(--icon);
	pointer-events: none;
}

.search-input
{
	width: 100%;
	font-family: var(--font-ui);
	font-size: var(--text-sm);
	color: var(--text);
	padding: 7px 10px 7px 30px;
	border: 1px solid var(--line);
	border-radius: var(--r-sm);
	background: var(--surface);
	outline: none;
}

.search-input:focus
{
	border-color: var(--interactive);
}

.panel-actions
{
	display: flex;
	align-items: center;
	gap: var(--sp-3);
	padding: 7px var(--sp-3);
	background: var(--surface-sunken);
	border-bottom: 1px solid var(--line-soft);
}

.link-action
{
	border: none;
	background: transparent;
	padding: 0;
	font-family: var(--font-ui);
	font-size: var(--text-chip);
	font-weight: 600;
	color: var(--interactive-text);
	cursor: pointer;
}

.link-action:hover
{
	text-decoration: underline;
}

.panel-count
{
	margin-left: auto;
	font-size: var(--text-chip);
	color: var(--text-3);
	white-space: nowrap;
}

.panel-list
{
	max-height: 260px;
	overflow-y: auto;
	padding: var(--sp-1) 0;
}

.option-row
{
	display: flex;
	width: 100%;
	padding: 5px var(--sp-3);
}

.option-row:hover
{
	background: var(--surface-sunken);
}

.option-row :deep(.lbl)
{
	font-size: var(--text-sm);
}

.option-empty
{
	padding: var(--sp-3);
	font-size: var(--text-sm);
	color: var(--text-3);
}
</style>
